import { useContext, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { AppContext } from '@/App.context';

export function useAdaptiveQueryCache(opts: AdaptiveQueryCacheOpts) {
	const { cacheTime, queryKey } = opts;
	const { onlineStatus: isOnline } = useContext(AppContext);
	const queryClient = useQueryClient();
	useEffect(() => {
		const query = queryClient.getQueryCache().find({ queryKey });
		if (!query) return;

		// --- Case A: No observers present ---
		if (query.getObserversCount() === 0 && isOnline) {
			queryClient.cancelQueries({ queryKey })
			queryClient.removeQueries({ queryKey });
			return;
		}

		const ttl = isOnline ? cacheTime : Infinity;

		// --- CASE B: Query has observers → allowed to update ---
		query.setOptions({ gcTime: ttl });

		// 2. Update every observer attached to the query
		query.observers.forEach((obs) => {
			obs.setOptions({
				...obs.options,
				gcTime: ttl,
				staleTime: ttl,
			});
		});

		if (isOnline) {
			queryClient.invalidateQueries({ queryKey });
		}
	}, [isOnline]);
}

interface AdaptiveQueryCacheOpts {
	cacheTime: number;
	queryKey: Array<any>;
}

export function useOfflineQueryPersistence({
	onlineDefaults = { staleTime: 0, gcTime: 5 * 60 * 1000 }, // adjust as needed
} = {}) {
	const queryClient = useQueryClient();

	useEffect(() => {
		function applyToAllQueries(options: {
			staleTime?: number;
			gcTime?: number;
			isOnline: boolean;
		}) {
			const queries = queryClient.getQueryCache().getAll();
			for (const query of queries) {
				const currentOptions = query.options ?? {};
				if (query.observers.length === 0 && options.isOnline) {
					queryClient.removeQueries({ queryKey: query.queryKey });
				}

				// IMPORTANT: must spread existing options or React Query ignores the update
				query.setOptions({
					...currentOptions,
					gcTime: options.gcTime,
				});
			}
		}

		const onOffline = () => {
			applyToAllQueries({
				staleTime: Infinity,
				gcTime: Infinity,
				isOnline: false,
			});
		};

		const onOnline = () => {
			applyToAllQueries({ ...onlineDefaults, isOnline: true });
		};

		window.addEventListener('offline', onOffline);
		window.addEventListener('online', onOnline);

		return () => {
			window.removeEventListener('offline', onOffline);
			window.removeEventListener('online', onOnline);
		};
	}, [queryClient, onlineDefaults]);
}
